import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import { ServicingBasePageComponent } from '@common/components/servicing-base-class';
import { ServicingFunctionIdType, ServicingPageIdType } from '@common/interfaces';
import { PREVIOUS_PAGE_IDENTIFIER } from '@common/interfaces/servicing-common';
import { LOAD_MASTERS } from '@common/master/master';
import { GetDocumentListStoreService, GetETicketItineraryReceiptStoreService } from '@common/services';
import {
  ETicketEmdHandler,
  ETicketEmdSelectTravelerInfo,
} from '@common/services/e-ticket-emd-select/e-ticket-emd-handler';
import { TravelerItem, toViewModel } from '@common/services/e-ticket-emd-select/emd-view-model';
import { ReceiptSearchService } from '@common/services/receipt-search/receipt-search.service';
import { ServicingCommonStoreService } from '@common/services/servicing-common-store/servicing-common-store.service';
import { SERVICING_ROUTES } from '@conf/routes.config';
import {
  AswContextType,
  AswServiceModel,
  DeviceType,
  ErrorType,
  NotRetryableErrorModel,
  PageType,
} from '@lib/interfaces';
import {
  AswContextStoreService,
  AswMasterService,
  AswServiceStoreService,
  CommonLibService,
  PageInitService,
  PageLoadingService,
} from '@lib/services';
import { Observable, ReplaySubject, combineLatest, race } from 'rxjs';
import { filter, map, take, zipWith } from 'rxjs/operators';
import { GetDocumentListRequest, GetETicketItineraryReceiptRequest } from 'src/sdk-servicing';
import { ErrorCodeConstants } from '@conf/app.constants';

/** 検索種別 */
export type SearchTypes = (typeof SearchTypes)[keyof typeof SearchTypes];
export const SearchTypes = {
  /** 航空券/EMD一覧 */
  ETICKET_EMD_0: 0,
  /** EMD一覧 */
  EMD_1: 1,
} as const;

/** 航空券PDF取得APIのエラーコード */
const GET_ETICKET_ITINERARY_RECEIPT_ERRORS: { [name: string]: string } = {
  // (指定航空券情報なし)
  [ErrorCodeConstants.ERROR_CODES.EBAZ000147]: 'E0353',
  // (取扱不可クーポンステータス)
  [ErrorCodeConstants.ERROR_CODES.EBAZ000163]: 'E0353',
} as const;

/**
 * 遷移元画面区分を元に検索種別を返す
 * @param previousPage 遷移元の画面区分
 * @returns 検索種別
 */
export function getSearchType(previousPage?: PREVIOUS_PAGE_IDENTIFIER): SearchTypes {
  return SearchTypes.ETICKET_EMD_0;
}

/**
 * 航空券/EMDリスト取得API用のリクエストパラメータを返す
 * @param aswServiceModel サービス共通の値
 * @returns 航空券/EMDリスト取得API呼び出し時に指定するリクエストパラメーター
 */
function getGetDocumentListParams(aswServiceModel: AswServiceModel): GetDocumentListRequest {
  const param: GetDocumentListRequest = {
    searchType: GetDocumentListRequest.SearchTypeEnum.ETicketAndEmdByPnrForETicketEmd,
    credential: {
      firstName: aswServiceModel.firstName!,
      lastName: aswServiceModel.lastName!,
    },
  };
  if (aswServiceModel.orderId) {
    param.orderId = aswServiceModel.orderId;
  } else {
    param.travelDocumentId = aswServiceModel.travelDocumentId;
  }
  return param;
}

/**
 * 航空券PDF取得API用のリクエストパラメーターを返す
 * @param aswServiceModel サービス共通の値
 * @param travelerInfo 搭乗者別航空券情報
 * @returns 航空券PDF取得API呼び出し時に指定するリクエストパラメーター
 */
function getETicketItineraryReceiptParams(
  aswServiceModel: AswServiceModel,
  travelerInfo: ETicketEmdSelectTravelerInfo
): GetETicketItineraryReceiptRequest {
  return {
    eTicketNumber: travelerInfo.eTicketNumber,
    orderId: aswServiceModel.orderId!,
    credential: {
      firstName: travelerInfo.travelerName.firstName ?? '',
      lastName: travelerInfo.travelerName.lastName ?? '',
    },
  };
}

@Component({
  selector: 'asw-e-ticket-emd-select-cont',
  templateUrl: './e-ticket-emd-select-cont.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GetDocumentListStoreService, GetETicketItineraryReceiptStoreService, ReceiptSearchService],
})
export class ETicketEmdSelectContComponent extends ServicingBasePageComponent {
  override functionId: ServicingFunctionIdType = ServicingFunctionIdType.DOCUMENT_DISPLAY;
  override pageId: ServicingPageIdType = ServicingPageIdType.EMD_SELECT;
  override isEnabledLogin: boolean = false;
  override isUpgrade: boolean = false;
  override pageTitleKey: string = 'label.document.showOrPrint';
  override isAutoInitEnd: boolean = false;

  searchType: SearchTypes = SearchTypes.ETICKET_EMD_0;
  filterItemList: Array<string> = [];

  travelerList: Array<ETicketEmdSelectTravelerInfo> = [];
  isInitEnd$: Observable<unknown>;
  searchType$: ReplaySubject<number> = new ReplaySubject<number>();
  /** PDFを表示する別タブ */
  private pdfWindow: WindowProxy | null = null;

  constructor(
    private _commonLibService: CommonLibService,
    private __pageInitService: PageInitService,
    private _changeDetector: ChangeDetectorRef,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _aswMasterService: AswMasterService,
    private _aswContextStoreService: AswContextStoreService,
    private _aswServiceStoreService: AswServiceStoreService,
    private _receiptSearchService: ReceiptSearchService,
    private _getETicketItineraryReceiptStoreService: GetETicketItineraryReceiptStoreService,
    private _getDocumentListStoreService: GetDocumentListStoreService,
    private _loadingSvc: PageLoadingService,
    private _router: Router
  ) {
    super(_commonLibService, __pageInitService);

    this.isInitEnd$ = this._getDocumentListStoreService.res$.pipe(filter(Boolean)).pipe(
      filter((each) => Boolean(each.data)),
      take(1)
    );
    this.params = combineLatest([this.searchType$]).pipe(
      map(([searchType]) => {
        // 動的文言条件に、EMD一覧のフィルタリング関係のパラメタをセットする
        return {
          searchType,
          // EMD一覧でなければ undefined のままにする
          selectedEmdStatus: undefined,
          filteredEmdList: undefined,
        };
      })
    );
  }

  override init(): void {
    this._getDocumentListStoreService.clearStore();

    this.subscribeService(this.isInitEnd$.pipe(take(1)), () => {
      this.__pageInitService.endInit(this.params);
    });

    this._getDocumentListStoreService.setupHandler(
      (response, _) => {
        // 6.1 航空券/EMD一覧で航空券一覧(ラジオボタンで航空券を選択時)を表示するためのリスト
        this.travelerList = ETicketEmdHandler.toTravelerList(response);
        this._changeDetector.markForCheck();
      },
      (apiErrorCode) => {
        if (apiErrorCode === ErrorCodeConstants.ERROR_CODES.EBAZ000727) {
          // 共通エラー画面に遷移する
          this._commonLibService.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.BUSINESS_LOGIC,
            errorMsgId: 'E0341',
            apiErrorCode: apiErrorCode,
          });
        } else {
          // 共通エラー画面に遷移する
          this._commonLibService.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.SYSTEM,
            apiErrorCode: apiErrorCode,
          });
        }
      },
      new Observable((observer) => {
        observer.next();
      })
    );

    // 検索種別
    const getDocumentListParams = getGetDocumentListParams(this._aswServiceStoreService.aswServiceData);
    this._getDocumentListStoreService.callApi(getDocumentListParams);
    this.searchType$.next(SearchTypes.ETICKET_EMD_0);

    this.handleGetETicketItineraryReceiptResponse();
  }
  override destroy(): void {
    this._servicingCommonStoreService.updateServicingCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.EMD_SELECT,
    });
  }
  override reload(): void {}

  /**
   * eチケット表示ボタン押下イベント処理
   * @param travler 選択された要素の位置の旅客情報
   */
  onClickETicketButton(travler: ETicketEmdSelectTravelerInfo) {
    this._loadingSvc.startLoading();
    if (!travler.isDisplayAvailable) {
      const errorInfo: NotRetryableErrorModel = {
        errorType: ErrorType.BUSINESS_LOGIC,
        errorMsgId: 'E0354',
      };
      this._commonLibService.errorsHandlerService.setNotRetryableError(errorInfo);
      return;
    }

    this.openPdfWindow();

    const params = getETicketItineraryReceiptParams(this._aswServiceStoreService.aswServiceData, travler);
    this._getETicketItineraryReceiptStoreService.callApi(params);
  }

  /**
   * 別タブにPDFを表示します。
   *
   * @param pdfBase64str PDFデータ
   */
  private showPdfInNewWindow(pdfBase64str: string, pdfName: string) {
    if (this.pdfWindow) {
      this._receiptSearchService.renderPdfViewToDocument(pdfBase64str, this.pdfWindow.document, pdfName);
    }
  }

  /**
   * 航空券PDF取得APIのレスポンスハンドリング
   */
  private handleGetETicketItineraryReceiptResponse(): void {
    this._getETicketItineraryReceiptStoreService.setupHandler(
      (response) => {
        const base64str: string = response.data.pdfData;
        const pdfName = this._aswMasterService.getMPropertyByKey(
          'reportForm',
          'downloadFileName.servicing.itineraryReceipt'
        );
        this.subscribeService(
          this._aswContextStoreService.getAswContextByKey$(AswContextType.DEVICE_TYPE),
          (result: any) => {
            if (result === DeviceType.SMART_PHONE) {
              this._receiptSearchService.downloadPDF(base64str, pdfName);
            } else {
              this.showPdfInNewWindow(base64str, pdfName);
              this.pdfWindow = null;
            }
          }
        );
        this._getETicketItineraryReceiptStoreService.clearStore();
        this._loadingSvc.endLoading();
      },
      (apiErrorCode?: string) => {
        this.pdfWindow?.close();
        this.pdfWindow = null;

        // 失敗処理
        if (apiErrorCode && apiErrorCode in GET_ETICKET_ITINERARY_RECEIPT_ERRORS) {
          const errorMessageId = GET_ETICKET_ITINERARY_RECEIPT_ERRORS[apiErrorCode];
          this._commonLibService.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.BUSINESS_LOGIC,
            errorMsgId: errorMessageId,
            apiErrorCode: apiErrorCode,
          });
        } else {
          this._commonLibService.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.SYSTEM,
            apiErrorCode,
          });
        }
        this._loadingSvc.endLoading();
      }
    );
  }

  /**
   * 端末種別がSP以外の場合に別タブを開く
   */
  private openPdfWindow(): void {
    this.pdfWindow =
      this._aswContextStoreService.aswContextData.deviceType === DeviceType.SMART_PHONE ? null : window.open('');
  }
}
